/**
 * BaseNode Abstract Class
 * All workflow nodes extend from this class
 * Contains all common state, handlers, and modal rendering logic
 */

import React, { Component } from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { WorkflowNode, getNodeDisplayProps, ProcessElementPort, SYSTEM_MY_COMPONENTS_PORT, SYSTEM_COMPONENT_PORT } from '@flow-studio/types';
import { CustomHandle } from '../../Handles/CustomHandle';
import { PackageIcon, renderFiIcon, NodeCategoryIcon } from '@bizfirst/common-react';
import { NodeHoverMenu } from '../Support/NodeHoverMenu';
import { NodePropertiesModal } from '../../Modals/NodePropertiesModal';
import { RenameNodeModal } from '../../Modals/RenameNodeModal';
import { RawConfigModal } from '../../RawConfig';
import { MoreOptionsMenu } from '../../Menus/MoreOptionsMenu';
import { NodeViewOptionsMenu } from '../../Menus/NodeViewOptionsMenu';
import { ConnectorConfigDialog } from '../../Modals/ConnectorConfigDialog';
import { ConfirmDialog } from '../../Support/ConfirmDialog';
import { useContextMenu } from '../../../hooks/useContextMenu';
import { useWorkflowStore, useUIStore, useDesignerModeStore } from '@flow-studio/store';
import type { WorkflowState } from '@flow-studio/store';
import type { UIStoreState } from '@flow-studio/store';
import { cloneNode } from '@flow-studio/services';
import { NodeResizerFeature } from '../../../features/node-resize';

// ─── localStorage collapse helpers ──────────────────────────────────────────
const LS_COLLAPSE_KEY = 'flow-studio.node-collapsed';

function getCollapseMap(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(LS_COLLAPSE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function persistNodeCollapse(nodeId: string, collapsed: boolean): void {
  try {
    const map = getCollapseMap();
    map[nodeId] = collapsed;
    localStorage.setItem(LS_COLLAPSE_KEY, JSON.stringify(map));
  } catch { /* ignore */ }
}

function readNodeCollapsed(nodeId: string): boolean {
  return getCollapseMap()[nodeId] ?? false;
}

/**
 * Configuration interface for BaseNode customization
 */
export interface NodeConfig {
  // Behavior flags
  showToolbarOnClick?: boolean;      // Show toolbar on click instead of hover
  showToolbarOnHover?: boolean;      // Show toolbar on hover
  enablePropertiesModal?: boolean;   // Enable properties modal
  enableRenameModal?: boolean;       // Enable rename modal
  enableMoreMenu?: boolean;          // Enable more options menu

  // Visual flags
  renderHandlesManually?: boolean;   // If true, child must render handles
  customClassName?: string;          // Additional CSS class

  // Confirmation
  confirmDelete?: boolean;           // Show confirmation on delete
}

/**
 * Base state for all nodes
 */
interface BaseNodeState {
  showPropertiesModal: boolean;
  showRenameModal: boolean;
  showMoreMenu: boolean;
  showConnectorConfig: boolean;
  showDeleteConfirm: boolean;
  isHovering: boolean;
  isCollapsed: boolean;
  showNodeSearchModal?: boolean;
  selectedSourceHandle?: string | null;
  showViewMenu: boolean;
  showRawConfig: boolean;
  localHiddenSatelliteIds: string[];
  localHiddenComponentIds: string[];
}

// portKeys that belong to the component system — handled by renderSystemHandles()
const SYSTEM_PORT_KEYS = new Set(['my-components', 'component']);

/**
 * Props with hooks injected
 */
interface BaseNodePropsWithHooks extends NodeProps<WorkflowNode['data']> {
  contextMenuHandler: ReturnType<typeof useContextMenu>['handleNodeContextMenu'];
  workflowStore: WorkflowState;
  uiStore: UIStoreState;
  designerMode: string;
}

/**
 * BaseNode Abstract Class
 *
 * All workflow nodes extend from this class.
 * Contains all common state, handlers, and modal rendering logic.
 */
export abstract class BaseNodeClass extends Component<
  BaseNodePropsWithHooks,
  BaseNodeState
> {
  /**
   * Configuration - Override in child classes
   */
  protected config(): NodeConfig {
    return {
      showToolbarOnClick: false,
      showToolbarOnHover: true,
      enablePropertiesModal: true,
      enableRenameModal: true,
      enableMoreMenu: true,
      renderHandlesManually: false,
      confirmDelete: true,
    };
  }

  // "⋮" trigger button — anchor for NodeViewOptionsMenu's viewport-aware portal positioning.
  private _viewMenuButtonRef: HTMLButtonElement | null = null;
  // Outer node container — used as the positioning anchor for edge-aware dropdowns (MoreOptionsMenu).
  private _nodeContainerRef: HTMLDivElement | null = null;

  constructor(props: BaseNodePropsWithHooks) {
    super(props);
    this.state = {
      showPropertiesModal: false,
      showRenameModal: false,
      showMoreMenu: false,
      showConnectorConfig: false,
      showDeleteConfirm: false,
      isHovering: false,
      isCollapsed: readNodeCollapsed(props.id),
      showViewMenu: false,
      showRawConfig: false,
      localHiddenSatelliteIds: [],
      localHiddenComponentIds: [],
    };
  }

  private _onCollapseAll = () => {
    persistNodeCollapse(this.props.id, true);
    this.setState({ isCollapsed: true });
  };

  private _onExpandAll = () => {
    persistNodeCollapse(this.props.id, false);
    this.setState({ isCollapsed: false });
  };

  private handleNodeResizeEnd = (width: number, height: number): void => {
    const config = this.props.data?.processElement?.configuration ?? {};
    this.props.workflowStore.updateProcessElement(this.props.id, {
      configuration: {
        ...config,
        designer: {
          ...config.designer,
          ui: {
            ...config.designer?.ui,
            width,
            height,
          },
        },
      },
    });
  };

  componentDidMount(): void {
    window.addEventListener('flow-studio:collapse-all', this._onCollapseAll);
    window.addEventListener('flow-studio:expand-all',   this._onExpandAll);
  }

  componentWillUnmount(): void {
    window.removeEventListener('flow-studio:collapse-all', this._onCollapseAll);
    window.removeEventListener('flow-studio:expand-all',   this._onExpandAll);
  }

  componentDidUpdate(prevProps: BaseNodePropsWithHooks, prevState: BaseNodeState): void {
    // Close menus when node is deselected
    if (prevProps.selected && !this.props.selected) {
      if (this.state.showMoreMenu) this.setState({ showMoreMenu: false });
      if (this.state.showViewMenu) this.setState({ showViewMenu: false });
    }
  }

  // ==================== Event Handlers ====================

  protected handleContextMenu = (e: React.MouseEvent): void => {
    this.props.contextMenuHandler(e, this.props.id, this.props.data);
  };

  protected handleDelete = (): void => {
    const { confirmDelete } = this.config();

    if (confirmDelete) {
      this.setState({ showDeleteConfirm: true });
    } else {
      this.props.workflowStore.deleteNode(this.props.id);
    }
  };

  private handleDeleteConfirmed = (): void => {
    const display = getNodeDisplayProps(this.props.data);
    this.props.workflowStore.deleteNode(this.props.id);
    this.setState({ showDeleteConfirm: false });
    this.props.uiStore.showToast('info', `Node "${display.displayName}" deleted`);
  };

  protected handleRun = (): void => {
    const { id, data } = this.props;
    this.onRun(id, data);
  };

  protected handleEdit = (): void => {
    this.setState({ showPropertiesModal: true });
  };

  protected handleMore = (e: React.MouseEvent): void => {
    e.stopPropagation();
    this.setState(prev => ({ showMoreMenu: !prev.showMoreMenu }));
  };

  protected handleRename = (): void => {
    this.setState({
      showRenameModal: true,
      showMoreMenu: false,
    });
  };

  protected handleDuplicate = (): void => {
    const { id } = this.props;
    const { nodes, addNode } = this.props.workflowStore;
    const node = nodes.find((n) => n.id === id);

    if (node) {
      addNode(cloneNode(node));
    }

    this.setState({ showMoreMenu: false });
  };

  protected handleConfigureConnector = (e?: React.MouseEvent): void => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    const { data } = this.props;
    const connectorId = getNodeDisplayProps(data).connectorID;

    if (!connectorId) {
      const { isDirty } = this.props.workflowStore;
      const msg = isDirty
        ? 'Please save the workflow first. A connector will be created automatically when you save.'
        : 'Connector not yet available for this node. Please save the workflow again.';
      console.warn(
        `[BaseNode] Connector not available for node "${getNodeDisplayProps(data).displayName}" (id=${this.props.id}, isDirty=${isDirty})`,
        { processElement: data.processElement }
      );
      this.props.uiStore.showToast('warning', msg);
      this.setState({ showMoreMenu: false });
      return;
    }

    this.setState({
      showConnectorConfig: true,
      showMoreMenu: false,
    });
  };

  protected handleToggleCollapse = (): void => {
    const next = !this.state.isCollapsed;
    persistNodeCollapse(this.props.id, next);
    this.setState({ isCollapsed: next });
  };

  /** Recursively find all satellite node IDs connected via management handles from startId */
  private findSatelliteIds(startId: string): string[] {
    const MANAGEMENT_HANDLES = ['team-leader', 'extends'];
    // Use getState() for freshest edges — not stale props snapshot
    const edges = (useWorkflowStore.getState() as any).edges as any[];
    const result: string[] = [];
    const queue = [startId];
    const visited = new Set<string>();

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);

      const children = edges
        .filter(e => e.source === current && MANAGEMENT_HANDLES.includes(e.sourceHandle ?? ''))
        .map(e => e.target as string);

      for (const child of children) {
        if (!result.includes(child)) result.push(child);
        queue.push(child);
      }
    }

    return result;
  }

  private findComponentIds(parentId: string): string[] {
    const edges = (useWorkflowStore.getState() as any).edges as any[];
    return edges
      .filter((e: any) => e.source === parentId && e.sourceHandle === 'my-components')
      .map((e: any) => e.target as string);
  }

  private handleToggleNodeComponents = (): void => {
    const freshStore = useUIStore.getState() as any;
    const { localHiddenComponentIds } = this.state;
    const storeHidden: string[] = freshStore.hiddenNodeIds ?? [];

    const anyActuallyHidden = localHiddenComponentIds.some((id: string) => storeHidden.includes(id));

    if (localHiddenComponentIds.length > 0 && anyActuallyHidden) {
      freshStore.showNodes(localHiddenComponentIds);
      this.setState({ localHiddenComponentIds: [] });
    } else {
      const ids = this.findComponentIds(this.props.id);
      if (ids.length > 0) {
        freshStore.hideNodes(ids);
        this.setState({ localHiddenComponentIds: ids });
      }
    }
  };

  private handleToggleNodeSatellites = (): void => {
    const freshStore = useUIStore.getState() as any;
    const { localHiddenSatelliteIds } = this.state;
    const storeHidden: string[] = freshStore.hiddenNodeIds ?? [];

    // Are any of this node's satellites actually hidden in the store right now?
    const anyActuallyHidden = localHiddenSatelliteIds.some((id: string) => storeHidden.includes(id));

    if (localHiddenSatelliteIds.length > 0 && anyActuallyHidden) {
      // Currently hidden — reveal them and clear local tracking
      freshStore.showNodes(localHiddenSatelliteIds);
      this.setState({ localHiddenSatelliteIds: [] });
    } else {
      // Either nothing hidden yet, or global reset already cleared them — re-hide
      const ids = this.findSatelliteIds(this.props.id);
      if (ids.length > 0) {
        freshStore.hideNodes(ids);
        this.setState({ localHiddenSatelliteIds: ids });
      }
    }
  };

  // ==================== Extension Points ====================

  /**
   * Override to customize run behavior
   */
  protected onRun(_id: string, data: WorkflowNode['data']): void {
    this.props.uiStore.showToast('info', `Running: ${data.processElement?.displayName}`);
  }

  /**
   * Render the node's main container styles
   * Override for custom styling
   */
  protected abstract getContainerStyle(): React.CSSProperties;

  /** Default node width when no config value is set. Override per node type. */
  protected getDefaultNodeWidth(): number { return 260; }
  /** Default node height when no config value is set. Override per node type. */
  protected getDefaultNodeHeight(): number { return 120; }
  /**
   * Return false for shape nodes (circle, diamond) whose layout depends on
   * equal or fixed proportions managed by getContainerStyle().
   */
  protected supportsConfigDimensions(): boolean { return true; }

  /**
   * Render the node's main content (header + body)
   * Must be implemented by child classes
   */
  protected abstract renderContent(): React.ReactNode;

  /**
   * Optional: Render custom class name
   */
  protected getClassName(): string {
    const { customClassName } = this.config();
    return customClassName || 'base-node';
  }

  // ==================== Common Rendering Methods ====================

  /**
   * Auto-distribute handles on the same side so they don't stack at center.
   * Only applies when a side has multiple ports with no explicit offsets set.
   */
  private distributePorts(ports: ProcessElementPort[]): ProcessElementPort[] {
    const SPACING = 34; // px between handles

    // Group by position side
    const byPosition: Record<string, ProcessElementPort[]> = {};
    for (const port of ports) {
      const side = port.position || (port.portType === 'input' ? 'left' : 'right');
      (byPosition[side] = byPosition[side] || []).push(port);
    }

    const result: ProcessElementPort[] = [];
    for (const [side, sidePorts] of Object.entries(byPosition)) {
      const n = sidePorts.length;
      if (n <= 1) { result.push(...sidePorts); continue; }

      const isVerticalSide = side === 'left' || side === 'right';
      const offsetField = isVerticalSide ? 'offsetY' : 'offsetX';

      // Respect explicit offsets only if they're already spaced at least SPACING apart —
      // otherwise ports render crowded together (seen with tightly-packed backend-seeded
      // defaults, e.g. the Loop node's Looping/Completed/Error outputs).
      const offsets = sidePorts.map(p => (p as any)[offsetField] ?? 0);
      const hasExplicit = offsets.some(o => o !== 0);
      const sortedOffsets = [...offsets].sort((a, b) => a - b);
      const isWellSpaced = sortedOffsets.every((o, i) => i === 0 || o - sortedOffsets[i - 1] >= SPACING);
      if (hasExplicit && isWellSpaced) { result.push(...sidePorts); continue; }

      // Evenly distribute centered at 0
      result.push(...sidePorts.map((port, i) => ({
        ...port,
        [offsetField]: Math.round((i - (n - 1) / 2) * SPACING),
      })));
    }

    return result;
  }

  /** Minimum node height in px needed to show all side handles without clipping */
  protected computeHandleMinHeight(): number {
    const { data } = this.props;
    const ports: ProcessElementPort[] = (data.processElement as any)?.ports ?? [];
    const SPACING = 34;
    const BASE = 52; // header + minimal body

    const leftCount  = ports.filter(p => p.position === 'left'  || (p.portType === 'input'  && !p.position)).length;
    const rightCount = ports.filter(p => p.position === 'right' || (p.portType === 'output' && !p.position)).length;
    const maxSide = Math.max(leftCount, rightCount);
    return Math.max(BASE, maxSide * SPACING + 48); // +48px padding buffer (top + bottom clearance for handle visuals)
  }

  /**
   * Render input handles
   */
  protected renderInputHandles(): React.ReactNode {
    if (this.config().renderHandlesManually) return null;

    const { id, data } = this.props;
    let { inputPorts } = getNodeDisplayProps(data);

    // Fallback: use nodeTypes from store when data.ports is empty
    if (inputPorts.length === 0) {
      const nodeTypeCode = data?.processElement?.configuration?.designer?.ui?.nodeType;
      if (nodeTypeCode) {
        const nodeType = this.props.workflowStore.nodeTypes.find(nt => nt.code === nodeTypeCode);
        if (nodeType?.defaultInputPorts?.length) {
          inputPorts = nodeType.defaultInputPorts.map((p, i) => ({ ...p, portIndex: i }));
        }
      }
    }

    // System ports are rendered by renderSystemHandles — exclude them here
    const filtered = inputPorts.filter(p => !SYSTEM_PORT_KEYS.has(p.portKey));

    // Last resort: render a default input handle for non-trigger nodes that have no ports defined
    if (filtered.length === 0 && !getNodeDisplayProps(data).isTrigger) {
      const defaultInputPort = {
        portKey: 'main',
        portType: 'input' as const,
        portIndex: 0,
        label: '',
        isRequired: false,
        isMultiple: false,
        isMainPort: true,
        isErrorPort: false,
        position: 'left' as const,
        handleStyle: 'arrow' as const,
        color: '#10b981',
      };
      return <CustomHandle key="input" port={defaultInputPort} nodeId={id} />;
    }

    return this.distributePorts(filtered).map((port) => (
      <CustomHandle key={port.portKey} port={port} nodeId={id} />
    ));
  }

  /**
   * Render output handles
   */
  protected renderOutputHandles(): React.ReactNode {
    if (this.config().renderHandlesManually) return null;

    const { id, data } = this.props;
    let { outputPorts } = getNodeDisplayProps(data);

    // System ports rendered separately
    const nonSystemOutput = outputPorts.filter(p => !SYSTEM_PORT_KEYS.has(p.portKey));

    if (nonSystemOutput.length > 0) {
      return this.distributePorts(nonSystemOutput).map((port) => (
        <CustomHandle key={port.portKey} port={port} nodeId={id} />
      ));
    }

    // Fallback: use nodeTypes from store when data.ports is empty
    const nodeTypeCode = data?.processElement?.configuration?.designer?.ui?.nodeType;
    if (nodeTypeCode) {
      const nodeType = this.props.workflowStore.nodeTypes.find(nt => nt.code === nodeTypeCode);
      if (nodeType?.defaultOutputPorts?.length) {
        outputPorts = nodeType.defaultOutputPorts.map((p, i) => ({ ...p, portIndex: i }));
        return this.distributePorts(outputPorts).map((port) => (
          <CustomHandle key={port.portKey} port={port} nodeId={id} />
        ));
      }
    }

    // Last resort: single generic output handle
    const defaultOutputPort = {
      portKey: 'main',
      portType: 'output' as const,
      portIndex: 0,
      label: '',
      isRequired: false,
      isMultiple: true,
      isMainPort: true,
      isErrorPort: false,
      position: 'right' as const,
      handleStyle: 'arrow' as const,
      color: '#10b981',
    };

    return <CustomHandle key="output" port={defaultOutputPort} nodeId={id} />;
  }

  /**
   * Render system-level component handles:
   *  • my-components (source, left-bottom) — only shown when there are component edges and satellites are visible
   *  • component (target, top-right) — shown when satellites are visible (allows any node to become a component)
   */
  protected renderSystemHandles(): React.ReactNode {
    const { id } = this.props;
    const uiStore = this.props.uiStore as any;
    const hideSatellites: boolean = uiStore.hideSatellites ?? false;
    const hideComponentsGlobally: boolean = uiStore.hideComponentsGlobally ?? true;

    if (hideComponentsGlobally || hideSatellites) return null;

    const hideUnassigned: boolean =
      uiStore.hideUnassignedPorts === true ||
      (uiStore.hiddenPortsNodeIds ?? []).includes(id);

    const edges = this.props.workflowStore.edges;
    const hasMyComponents = edges.some((e: any) => e.source === id && e.sourceHandle === 'my-components');
    const hasComponent    = edges.some((e: any) => e.target === id && e.targetHandle === 'component');

    return (
      <>
        {(!hideUnassigned || hasMyComponents) && <CustomHandle key="my-components" port={SYSTEM_MY_COMPONENTS_PORT} nodeId={id} />}
        {(!hideUnassigned || hasComponent)    && <CustomHandle key="component"     port={SYSTEM_COMPONENT_PORT}    nodeId={id} />}
      </>
    );
  }

  /**
   * Render hover menu / toolbar
   */
  protected renderToolbar(): React.ReactNode {
    const { id, data, selected } = this.props;
    const { isHovering } = this.state;
    const { showToolbarOnClick, showToolbarOnHover } = this.config();

    const shouldShow = (showToolbarOnClick && selected) || (showToolbarOnHover && isHovering);

    if (!shouldShow) return null;

    const display = getNodeDisplayProps(data);

    return (
      <NodeHoverMenu
        nodeId={id}
        onDelete={this.handleDelete}
        onRun={this.handleRun}
        onEdit={this.handleEdit}
        onConfigureConnector={this.handleConfigureConnector}
        onMore={this.handleMore}
        helpUrl={display.helpUrl}
      />
    );
  }

  /**
   * Render view menu button — ⋮ opens a dropdown with per-node view options
   */
  protected renderCollapseButton(): React.ReactNode {
    const { isCollapsed, showViewMenu, localHiddenSatelliteIds, localHiddenComponentIds } = this.state;
    const uiStore = this.props.uiStore as any;
    const hiddenPortsNodeIds: string[] = uiStore.hiddenPortsNodeIds ?? [];
    const portsHidden = hiddenPortsNodeIds.includes(this.props.id);
    // Derive from store's actual hiddenNodeIds — global reset clears the store but not local state
    const storeHiddenNodeIds: string[] = uiStore.hiddenNodeIds ?? [];
    const satellitesHidden = localHiddenSatelliteIds.length > 0 &&
      localHiddenSatelliteIds.some((id: string) => storeHiddenNodeIds.includes(id));
    const componentsHidden = localHiddenComponentIds.length > 0 &&
      localHiddenComponentIds.some((id: string) => storeHiddenNodeIds.includes(id));

    return (
      <div style={{ position: 'absolute', top: '4px', right: '4px', zIndex: 30 }}>
        {/* ⋮ button */}
        <button
          ref={r => { this._viewMenuButtonRef = r; }}
          className="nodrag"
          data-view-menu-trigger
          onClick={(e) => { e.stopPropagation(); this.setState({ showViewMenu: !showViewMenu }); }}
          onMouseDown={(e) => e.stopPropagation()}
          title="View options"
          style={{
            width: '18px', height: '16px', borderRadius: '3px',
            background: showViewMenu ? 'rgba(48,155,72,0.4)' : 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: showViewMenu ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)',
            fontSize: '12px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0, flexShrink: 0,
            transition: 'background 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => { const b = e.currentTarget as HTMLButtonElement; if (!showViewMenu) { b.style.background = 'rgba(255,255,255,0.15)'; b.style.color = 'rgba(255,255,255,0.9)'; } }}
          onMouseLeave={(e) => { const b = e.currentTarget as HTMLButtonElement; if (!showViewMenu) { b.style.background = 'rgba(255,255,255,0.08)'; b.style.color = 'rgba(255,255,255,0.5)'; } }}
        >
          ⋮
        </button>

        {/* Dropdown — portaled to document.body, viewport-clamped (see NodeViewOptionsMenu) */}
        {showViewMenu && (
          <NodeViewOptionsMenu
            anchorEl={this._viewMenuButtonRef}
            portsHidden={portsHidden}
            satellitesHidden={satellitesHidden}
            componentsHidden={componentsHidden}
            isCollapsed={isCollapsed}
            onToggleHidePorts={() => { (useUIStore.getState() as any).toggleNodePortHide(this.props.id); this.setState({ showViewMenu: false }); }}
            onToggleHideSatellites={() => { this.handleToggleNodeSatellites(); this.setState({ showViewMenu: false }); }}
            onToggleHideComponents={() => { this.handleToggleNodeComponents(); this.setState({ showViewMenu: false }); }}
            onHideNode={() => { (useUIStore.getState() as any).hideNodes([this.props.id]); this.setState({ showViewMenu: false }); }}
            onToggleCollapse={() => { this.handleToggleCollapse(); this.setState({ showViewMenu: false }); }}
            onReset={() => {
              const freshStore = useUIStore.getState() as any;
              if (localHiddenSatelliteIds.length > 0) freshStore.showNodes(localHiddenSatelliteIds);
              if (localHiddenComponentIds.length > 0) freshStore.showNodes(localHiddenComponentIds);
              if (portsHidden) freshStore.toggleNodePortHide(this.props.id);
              persistNodeCollapse(this.props.id, false);
              this.setState({ isCollapsed: false, localHiddenSatelliteIds: [], localHiddenComponentIds: [], showViewMenu: false });
            }}
            onClose={() => this.setState({ showViewMenu: false })}
          />
        )}
      </div>
    );
  }

  /**
   * Render all modals
   */
  protected renderModals(): React.ReactNode {
    const { id, data } = this.props;
    const {
      showPropertiesModal,
      showRenameModal,
      showMoreMenu,
      showConnectorConfig,
    } = this.state;
    const config = this.config();
    const display = getNodeDisplayProps(data);

    const connectorId = display.connectorID;
    const processElementId: number | undefined = display.processElementID;

    return (
      <>
        {/* Properties Modal */}
        {config.enablePropertiesModal && showPropertiesModal && (
          <NodePropertiesModal
            nodeId={id}
            onClose={() => this.setState({ showPropertiesModal: false })}
          />
        )}

        {/* Rename Modal */}
        {config.enableRenameModal && showRenameModal && (
          <RenameNodeModal
            nodeId={id}
            currentName={display.displayName}
            onClose={() => this.setState({ showRenameModal: false })}
          />
        )}

        {/* More Options Menu */}
        {config.enableMoreMenu && showMoreMenu && (
          <MoreOptionsMenu
            nodeId={id}
            anchorEl={this._nodeContainerRef}
            onClose={() => this.setState({ showMoreMenu: false })}
            onRename={this.handleRename}
            onEdit={() => {
              this.setState({
                showPropertiesModal: true,
                showMoreMenu: false,
              });
            }}
            onConfigureConnector={this.handleConfigureConnector}
            onDuplicate={this.handleDuplicate}
            onDelete={this.handleDelete}
            onRawConfig={() => this.setState({ showRawConfig: true, showMoreMenu: false })}
          />
        )}

        {/* Connector Configuration Dialog — opens for connector nodes AND logic/transform nodes */}
        {showConnectorConfig && (connectorId || processElementId) && (
          <ConnectorConfigDialog
            connectorId={connectorId}
            processElementId={processElementId}
            isOpen={showConnectorConfig}
            onClose={() => this.setState({ showConnectorConfig: false })}
            initialFormValues={data.processElement?.configuration?.nodeFormValues ?? {}}
            nodeCategory={display.category || ''}
            isTrigger={display.isTrigger}
            triggerType={display.triggerType || ''}
            isManual={display.triggerType === 'manual'}
            acceptedCredentialTypes={(() => {
              const ui = data.processElement?.configuration?.designer?.ui as any;
              const storedDtId = ui?.dataTemplateID as number | undefined;
              const nodeTypeCode = ui?.nodeType as string | undefined;
              const nodeType = storedDtId
                ? (this.props.workflowStore.nodeTypes.find(t => t.id === storedDtId) ?? this.props.workflowStore.nodeTypes.find(t => t.code === nodeTypeCode))
                : this.props.workflowStore.nodeTypes.find(t => t.code === nodeTypeCode);
              return nodeType?.acceptedCredentialTypes
                ?? this.props.workflowStore.nodeCategories.find(c => c.code === display.category)?.acceptedCredentialTypes
                ?? (data.processElement?.connector as any)?.configuration?.acceptedCredentialTypes;
            })()}
            nodeInfo={{
              id,
              type: this.props.type ?? '',
              backendId: processElementId,
              data: {
                displayName: display.displayName,
                description: display.description,
                category: display.category || '',
                connectorId: display.connectorID,
                icon: display.icon,
                headerImageUrl: display.headerImageUrl,
                helpUrl: display.helpUrl,
              },
            }}
            onNodeInfoSave={(displayName, description) => {
              this.props.workflowStore.updateProcessElement(id, { displayName, description });
            }}
            onSave={(updatedConnector) => {
              // Parsed locally only — for extracting outputPorts below. Deliberately NOT attached back
              // onto updates.connector: doing so used to add a lowercase "configuration" (object) key
              // alongside the connector's real "Configuration" (string) key on the same object. That
              // object is later spread wholesale into the PUT body workflowStore sends to
              // connectorApiClient.update — wrapRequest's convertKeysToPascalCase folds both
              // differently-cased keys into a single wire "Configuration" property, and since the
              // lowercase key iterated last, its un-stringified object value silently overwrote the
              // correct string, which the backend then rejected ("could not be converted to
              // Connector.Configuration (String)") on every subsequent connector save for this node.
              // updatedConnector.Configuration (the string) is preserved as-is on the connector object
              // stored below; workflowStore's own connector-save step now safely JSON.parses it when it
              // needs the object form, so nothing here needs to duplicate it.
              const configuration = typeof updatedConnector.Configuration === 'string'
                ? JSON.parse(updatedConnector.Configuration)
                : (updatedConnector.Configuration ?? {});
              // Resolve connectorID from either casing (API returns PascalCase,
              // but guard against camelCase too). Never overwrite an existing
              // connectorID with undefined — that would break subsequent Config opens.
              const resolvedConnectorID = updatedConnector.ConnectorID ?? (updatedConnector as any).connectorId;
              const updates: Record<string, any> = {
                connector: { ...updatedConnector },
              };
              if (resolvedConnectorID) {
                updates.connectorID = resolvedConnectorID;
              }
              // The live Output Ports grid control saves its rows under "outputPorts" —
              // "output_ports_grid" is the key used by the reference form-schema docs
              // (binoyDocs/atlas-forms/docs) but does not match what's actually persisted.
              // Check both so either schema variant is honored.
              const gridPorts = configuration.outputPorts ?? configuration.output_ports_grid;
              if (Array.isArray(gridPorts) && gridPorts.length > 0) {
                const inputPorts = (data.processElement?.ports ?? []).filter((p: any) => p.portType === 'input');
                updates.ports = [
                  ...inputPorts,
                  ...gridPorts.map((gp: any, i: number) => ({
                    portKey: gp.port || gp.usage,
                    portType: 'output' as const,
                    portIndex: i,
                    label: gp.label || gp.usage,
                    displayName: gp.label || gp.usage,
                    isRequired: false,
                    isMultiple: true,
                    isMainPort: gp.isDefault === true || gp.isDefault === 'true',
                    isErrorPort: false,
                    color: (gp.isDefault === true || gp.isDefault === 'true') ? '#10b981' : '#6366f1',
                    position: 'right' as const,
                    handleStyle: 'circle' as const,
                  })),
                ];
              }
              this.props.workflowStore.updateProcessElement(id, updates);
              this.setState({ showConnectorConfig: false });
            }}
            onFormValuesSave={(values) => {
              const currentConfig = data.processElement?.configuration ?? {};
              // Merge onto the configuration ROOT too — executors read fields like
              // fieldPolicy/guardRails/engagementConfig/assignedTo directly off the
              // Configuration root, not from a nested nodeFormValues wrapper. See the
              // matching fix in NodePropertiesModal.tsx's onFormValuesSave.
              const updates: Record<string, any> = {
                configuration: {
                  ...currentConfig,
                  ...values,
                  nodeFormValues: { ...currentConfig.nodeFormValues, ...values },
                },
              };
              const gridPorts = values.outputPorts ?? values.output_ports_grid;
              if (Array.isArray(gridPorts) && gridPorts.length > 0) {
                const inputPorts = (data.processElement?.ports ?? []).filter((p: any) => p.portType === 'input');
                updates.ports = [
                  ...inputPorts,
                  ...gridPorts.map((gp: any, i: number) => ({
                    portKey: gp.port || gp.usage,
                    portType: 'output' as const,
                    portIndex: i,
                    label: gp.label || gp.usage,
                    displayName: gp.label || gp.usage,
                    isRequired: false,
                    isMultiple: true,
                    isMainPort: gp.isDefault === true || gp.isDefault === 'true',
                    isErrorPort: false,
                    color: (gp.isDefault === true || gp.isDefault === 'true') ? '#10b981' : '#6366f1',
                    position: 'right' as const,
                    handleStyle: 'circle' as const,
                  })),
                ];
              }
              this.props.workflowStore.updateProcessElement(id, updates);
            }}
          />
        )}

        {/* Raw Config Modal */}
        {this.state.showRawConfig && (
          <RawConfigModal
            nodeId={id}
            onClose={() => this.setState({ showRawConfig: false })}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {this.state.showDeleteConfirm && (() => {
          const display = getNodeDisplayProps(this.props.data);
          return (
            <ConfirmDialog
              isOpen={this.state.showDeleteConfirm}
              onClose={() => this.setState({ showDeleteConfirm: false })}
              onConfirm={this.handleDeleteConfirmed}
              title="Delete Node"
              message={`Are you sure you want to delete "${display.displayName}"? This cannot be undone.`}
              confirmText="Delete"
              cancelText="Cancel"
              type="danger"
            />
          );
        })()}
      </>
    );
  }

  // ==================== Final Render ====================

  render(): React.ReactNode {
    const { data } = this.props;

    if (!data?.processElement || !data?.runtime) {
      return <div style={{ padding: '8px', color: 'red', fontSize: '11px', border: '1px dashed red', borderRadius: '6px', background: 'rgba(255,0,0,0.05)' }}>Invalid node data</div>;
    }

    const containerStyle = this.getContainerStyle();
    const className = this.getClassName();
    const isCollapsed = this.state.isCollapsed;

    const isSatellite = (data as any).isSatellite === true;

    const finalContainerStyle: React.CSSProperties = (() => {
      if (!this.supportsConfigDimensions() || isCollapsed) return { ...containerStyle, overflow: 'visible' };
      const uiConf = (data?.processElement?.configuration?.designer?.ui ?? {}) as any;
      const w = uiConf.width  ?? this.getDefaultNodeWidth();
      const h = uiConf.height ?? this.getDefaultNodeHeight();
      return {
        ...containerStyle,
        width: w, height: h,
        minWidth: w, minHeight: h, maxHeight: h,
        // visible so that handles at edges are never clipped; body handles its own overflow
        overflow: 'visible',
        boxSizing: 'border-box',
      };
    })();

    return (
      <React.Fragment>
      <NodeResizerFeature
        selected={this.props.selected ?? false}
        onResizeEnd={this.handleNodeResizeEnd}
      />
      <div
        ref={r => { this._nodeContainerRef = r; }}
        style={finalContainerStyle}
        className={`${className} bf-node`}
        onContextMenu={this.handleContextMenu}
        onMouseEnter={() => this.setState({ isHovering: true })}
        onMouseLeave={() => this.setState({ isHovering: false })}
      >
        {/* Collapse Button */}
        {this.renderCollapseButton()}

        {/* Satellite indicator badge */}
        {isSatellite && (
          <div
            title="Satellite node"
            style={{
              position: 'absolute',
              top: '4px',
              right: '28px',
              width: '16px',
              height: '15px',
              borderRadius: '4px',
              background: '#f97316',
              border: '1px solid rgba(255,255,255,0.3)',
              zIndex: 25,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="10" width="6" height="4" rx="1"/>
              <rect x="2" y="9" width="5" height="6" rx="1"/>
              <line x1="7" y1="12" x2="9" y2="12"/>
              <rect x="17" y="9" width="5" height="6" rx="1"/>
              <line x1="15" y1="12" x2="17" y2="12"/>
              <line x1="13" y1="10" x2="16" y2="6"/>
              <circle cx="16.5" cy="5.5" r="1.5"/>
            </svg>
          </div>
        )}

        {/* Input Handles */}
        {this.renderInputHandles()}

        {/* Node Content (header + body) - conditionally rendered */}
        {!isCollapsed && this.renderContent()}

        {/* Collapsed View - minimal display */}
        {isCollapsed && this.renderCollapsedContent()}

        {/* Output Handles */}
        {this.renderOutputHandles()}

        {/* System Handles (component ports) */}
        {this.renderSystemHandles()}

        {/* Toolbar */}
        {this.renderToolbar()}

        {/* Modals */}
        {this.renderModals()}
      </div>
      </React.Fragment>
    );
  }

  /**
   * Render collapsed content - minimal view
   */
  protected renderCollapsedContent(): React.ReactNode {
    const { data } = this.props;
    const { theme } = this.props.uiStore;

    const collapsedStyle: React.CSSProperties = {
      padding: '8px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: theme === 'dark' ? '#fff' : '#000',
      fontSize: '13px',
      fontWeight: 600,
    };

    const display = getNodeDisplayProps(data);
    const fiIcon = display.icon ? renderFiIcon(display.icon, 16, theme === 'dark' ? '#fff' : '#000') : null;
    return (
      <div style={collapsedStyle}>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {fiIcon ?? <NodeCategoryIcon category={display.category} size={16} color={theme === 'dark' ? '#fff' : '#374151'} />}
        </span>
        <span>{display.displayName}</span>
      </div>
    );
  }
}

/**
 * HOC to inject hooks into class component
 * React hooks can't be used directly in class components
 */
export function withNodeHooks<P extends NodeProps<WorkflowNode['data']>>(
  WrappedComponent: React.ComponentType<BaseNodePropsWithHooks>
) {
  return (props: P) => {
    // Nodes handle their own onContextMenu (see BaseNode.handleContextMenu) and stop
    // propagation, so ReactFlow's own onNodeContextMenu prop never fires for them — this
    // is the actual open path. It needs the live instance so the menu can be anchored in
    // flow-space (see useContextMenu) and stay attached to the node while panning/zooming.
    const reactFlowInstance = useReactFlow();
    const { handleNodeContextMenu } = useContextMenu(reactFlowInstance);
    const workflowStore = useWorkflowStore();
    const uiStore = useUIStore();
    const designerMode = useDesignerModeStore((s: any) => s.mode);

    return (
      <WrappedComponent
        {...props}
        contextMenuHandler={handleNodeContextMenu}
        workflowStore={workflowStore}
        uiStore={uiStore}
        designerMode={designerMode}
      />
    );
  };
}
